import { useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { cn } from "@postman/core/cn";
import { adminQuery, auditQuery, useAdminMutations, type AdminView } from "@/lib/admin";
import { meQueryOptions } from "@/lib/session";
import {
  Button,
  Callout,
  Card,
  Empty,
  Input,
  Label,
  PageTitle,
  Pill,
  Rows,
} from "@/components/ui";

export const Route = createFileRoute("/_auth/admin")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (me?.user.role !== "admin") throw redirect({ to: "/dashboard" });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(adminQuery),
  component: Admin,
});

/**
 * Admin.
 *
 * Three things live here and only one of them is dangerous. Cycles and grants
 * are ordinary edits; ACTIVATING a cycle changes what every coordinator can
 * see and what every applicant list contains, in one statement, because
 * everything in this system resolves against the active cycle. So that button
 * asks first, and the audit log below records it either way.
 */
function Admin() {
  const { data } = useSuspenseQuery(adminQuery);

  return (
    <div className="flex flex-col gap-12">
      <PageTitle
        eyebrow="Admin"
        title="Cycles and coordinators"
        lede="Coordinator is a grant of one vertical in one cycle — never a global role. Opening a new cycle retires every grant in the old one without anyone revoking anything."
      />

      <Cycles data={data} />
      <Grants data={data} />
      <Admins data={data} />
      <Audit />
    </div>
  );
}

function Cycles({ data }: { data: AdminView }) {
  const { createCycle, activateCycle } = useAdminMutations();
  const [name, setName] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <section className="flex flex-col gap-5">
      <h2 className="mono text-ink-3">Cycles</h2>

      <Rows>
        {data.cycles.map((cycle) => (
          <div
            key={cycle.id}
            className={cn(
              "flex flex-wrap items-center justify-between gap-4 px-4 py-4",
              cycle.active && "bg-paper-3",
            )}
          >
            <div className="flex items-center gap-3">
              <span className="text-[16px]">{cycle.name}</span>
              {cycle.active ? <Pill tone="live">Active</Pill> : null}
            </div>

            {cycle.active ? (
              <span className="mono text-ink-3">Everything resolves against this</span>
            ) : confirming === cycle.id ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="max-w-[42ch] text-[14px] text-ink-2">
                  This retires every coordinator grant in{" "}
                  {data.cycles.find((c) => c.active)?.name ?? "the current cycle"} and
                  empties every applicant list until new grants are made.
                </span>
                <Button
                  variant="danger"
                  className="h-9 px-3"
                  disabled={activateCycle.isPending}
                  onClick={() =>
                    activateCycle.mutate(cycle.id, { onSuccess: () => setConfirming(null) })
                  }
                >
                  Activate anyway
                </Button>
                <Button variant="ghost" className="h-9 px-3" onClick={() => setConfirming(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="h-9 px-3"
                onClick={() => setConfirming(cycle.id)}
              >
                Make active
              </Button>
            )}
          </div>
        ))}
      </Rows>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[16rem] flex-1 flex-col gap-2">
          <Label htmlFor="cycle-name">New cycle</Label>
          <Input
            id="cycle-name"
            value={name}
            maxLength={120}
            placeholder="2027 intake"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button
          disabled={!name.trim() || createCycle.isPending}
          onClick={() => createCycle.mutate(name.trim(), { onSuccess: () => setName("") })}
        >
          Create
        </Button>
      </div>

      {createCycle.error ? (
        <Callout tone="error">{(createCycle.error as Error).message}</Callout>
      ) : null}
      {activateCycle.error ? (
        <Callout tone="error">{(activateCycle.error as Error).message}</Callout>
      ) : null}
    </section>
  );
}

function Grants({ data }: { data: AdminView }) {
  const { grant, revoke } = useAdminMutations();
  const [target, setTarget] = useState<string | null>(null);
  const [email, setEmail] = useState("");

  if (!data.activeCycleId) {
    return (
      <section className="flex flex-col gap-5">
        <h2 className="mono text-ink-3">Coordinators</h2>
        <Empty>No cycle is active, so there is nothing to grant against.</Empty>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <h2 className="mono text-ink-3">Coordinators, this cycle</h2>

      <div className="flex flex-col gap-4">
        {data.verticals.map((v) => (
          <Card key={v.slug} className="flex flex-col gap-5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h3 className="text-[17px]">{v.name}</h3>
                <Count
                  n={
                    (v.counts.active ?? 0) +
                    (v.counts.selected ?? 0) +
                    (v.counts.rejected ?? 0)
                  }
                  one="applicant"
                  many="applicants"
                />
              </div>
              <Link
                to="/coord/$vertical"
                params={{ vertical: v.slug }}
                className="mono text-ink-3 underline underline-offset-4 hover:text-ink"
              >
                Open
              </Link>
            </div>

            {v.coordinators.length === 0 ? (
              <p className="mono text-ink-3">Nobody — this vertical has no coordinator</p>
            ) : (
              <ul className="flex flex-wrap gap-x-6 gap-y-3">
                {v.coordinators.map((c) => (
                  <li key={c.id} className="flex items-center gap-3">
                    <span className="text-[15px]">{c.name}</span>
                    <span className="mono text-ink-3">{c.bitsId ?? c.email}</span>
                    <button
                      className="mono text-ink-3 underline underline-offset-4 hover:text-[#B3261E]"
                      onClick={() => revoke.mutate({ vertical: v.slug, userId: c.id })}
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {target === v.slug ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex min-w-[18rem] flex-1 flex-col gap-2">
                  <Label htmlFor={`grant-${v.slug}`}>Institute email</Label>
                  <Input
                    id={`grant-${v.slug}`}
                    value={email}
                    placeholder="f20230123@pilani.bits-pilani.ac.in"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button
                  className="h-9 px-3"
                  disabled={!email.trim() || grant.isPending}
                  onClick={() =>
                    grant.mutate(
                      { vertical: v.slug, email: email.trim() },
                      { onSuccess: () => { setEmail(""); setTarget(null); } },
                    )
                  }
                >
                  Grant
                </Button>
                <Button variant="ghost" className="h-9 px-3" onClick={() => setTarget(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <button
                className="mono self-start text-ink-3 underline underline-offset-4 hover:text-ink"
                onClick={() => { setTarget(v.slug); setEmail(""); }}
              >
                Add a coordinator
              </button>
            )}
          </Card>
        ))}
      </div>

      {grant.error ? <Callout tone="error">{(grant.error as Error).message}</Callout> : null}
      {revoke.error ? <Callout tone="error">{(revoke.error as Error).message}</Callout> : null}
    </section>
  );
}

function Admins({ data }: { data: AdminView }) {
  return (
    <section className="flex flex-col gap-5">
      <h2 className="mono text-ink-3">Admins</h2>
      <p className="max-w-[62ch] text-[15px] leading-relaxed text-ink-2">
        Seeded from <span className="mono">ADMIN_EMAILS</span> the first time
        each of them signs in. There is no button here for a reason: a role that
        can grant itself anything should not be grantable from inside the thing
        it administers.
      </p>
      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {data.admins.map((a) => (
          <li key={a.id} className="mono text-ink-3">
            {a.email}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Audit() {
  const [open, setOpen] = useState(false);
  const { data, isPending } = useQuery({ ...auditQuery(100), enabled: open });

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="mono text-ink-3">Audit log</h2>
        <button
          className="mono text-ink-3 underline underline-offset-4 hover:text-ink"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Hide" : "Show the last 100"}
        </button>
      </div>

      {!open ? (
        <p className="max-w-[62ch] text-[15px] leading-relaxed text-ink-2">
          Every decision, grant and non-owner file read, with who did it. Nothing
          in the API writes to it from a request — each row is written by the
          code that took the action it describes, which is what makes it worth
          reading.
        </p>
      ) : isPending ? (
        <p className="mono text-ink-3">Loading…</p>
      ) : !data || data.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <Rows className="max-h-[60vh] overflow-y-auto">
          {data.map((row) => (
            <div key={row.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3">
              <span className="mono text-ink-3">
                {row.createdAt
                  ? new Date(row.createdAt).toLocaleString("en-IN", {
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : ""}
              </span>
              <span className="mono">{row.action}</span>
              <span className="text-[14px] text-ink-2">{row.actorEmail ?? "system"}</span>
              <span className="mono text-ink-3">
                {row.subjectType}
                {row.subjectId ? ` ${row.subjectId.slice(0, 8)}` : ""}
              </span>
            </div>
          ))}
        </Rows>
      )}
    </section>
  );
}

/** "1 applicant", not "1 applicants". */
function Count({ n, one, many }: { n: number; one: string; many: string }) {
  return (
    <span className="mono text-ink-3">
      {n} {n === 1 ? one : many}
    </span>
  );
}
